import { ApolloError } from '@apollo/client';
import * as Sentry from '@sentry/react';
import { useMemo } from 'react';
import {
  GetSupportedActionsQuery,
  useGetSupportedActionsQuery,
} from '../types';

export type JsonSchemaProperty = {
  type: string;
  description?: string;
  enum?: string[];
  pattern?: string;
  minimum?: number;
  maximum?: number;
  minItems?: number;
  items?: { type: string };
  properties?: Record<string, JsonSchemaProperty>;
  required?: string[];
  additionalProperties?: boolean | Record<string, unknown>;
};

export type ActionJsonSchema = {
  type: string;
  properties: Record<string, JsonSchemaProperty>;
  required?: string[];
  additionalProperties?: boolean;
};

export type ActionInfo = {
  type: string;
  name: string;
  description: string;
  category: string;
  supportsNesting: boolean;
  availableScopes: string[];
  metadata: { schema: ActionJsonSchema; example: Record<string, unknown> };
  documentation?: {
    usageNotes?: string;
    requiredServices?: string[];
    relatedActions?: string[];
  };
};

type RawActionItem =
  GetSupportedActionsQuery['getSupportedActions']['actions'][number];

const isValidActionJsonSchema = (value: unknown): value is ActionJsonSchema => {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v['type'] === 'string' &&
    typeof v['properties'] === 'object' &&
    v['properties'] !== null
  );
};

const mapToActionInfo = (raw: RawActionItem): ActionInfo | null => {
  const { schema, example } = raw.metadata;

  if (!isValidActionJsonSchema(schema)) {
    Sentry.captureException(
      new Error(
        `Skipping action "${raw.type}": unexpected metadata.schema shape — missing "type" or "properties".`,
      ),
    );
    return null;
  }

  return {
    type: raw.type,
    name: raw.name,
    description: raw.description,
    category: raw.category,
    supportsNesting: raw.supportsNesting,
    availableScopes: raw.availableScopes,
    metadata: {
      schema,
      example:
        example !== null && typeof example === 'object'
          ? (example as Record<string, unknown>)
          : {},
    },
    documentation: raw.documentation
      ? {
          usageNotes: raw.documentation.usageNotes ?? undefined,
          requiredServices: raw.documentation.requiredServices ?? undefined,
          relatedActions: raw.documentation.relatedActions ?? undefined,
        }
      : undefined,
  };
};

interface UseSupportedActionsReturn {
  actions: ActionInfo[];
  loading: boolean;
  error: ApolloError | undefined;
}

export const useSupportedActions = (): UseSupportedActionsReturn => {
  const { data, loading, error } = useGetSupportedActionsQuery();
  const actions = useMemo(
    () =>
      (data?.getSupportedActions?.actions ?? [])
        .map(mapToActionInfo)
        .filter((a): a is ActionInfo => a !== null),
    [data],
  );
  return { actions, loading, error };
};
